import { Flex } from "@chakra-ui/react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { usePapaParse } from "react-papaparse";
import { graphql, useFragment } from "react-relay";

import { DailyReportTable_site$key } from "~/src/__relay_artifacts__/DailyReportTable_site.graphql";
import { Table } from "~/src/components/common/tables/Table";

import { CSVDownloadLink } from "../CSVDownloadLink";
import { DisableColumnSelectButton } from "../DisableColumnSelectButton";
import {
  ColumnData,
  ReportTableDataRow,
  ReportTableHeaderRow,
} from "../ReportTable";
import { RowHeightSettingButton } from "../RowHeightSettingButton";
import { disableColumnOptions, headerOptionsForDailyReport } from "../utils";

const fragment = graphql`
  fragment DailyReportTable_site on Site {
    reportDaily(input: $input)
  }
`;

export type Prop = {
  onSortClick: (params: any) => void;
  siteRef: DailyReportTable_site$key;
};

export const DailyReportTable: FC<
  React.PropsWithChildren<React.PropsWithChildren<Prop>>
> = ({ siteRef, onSortClick }) => {
  const { reportDaily } = useFragment<typeof siteRef>(fragment, siteRef);
  const [disableColumns, setDisableColumns] = useState<number[]>([]);
  const [rowHeight, setRowHeight] = useState<number>(56);

  const { readString } = usePapaParse();

  const [data, setData] = useState<{ headers: string[]; rows: string[][] }>({
    headers: [],
    rows: [],
  });

  useEffect(() => {
    readString(reportDaily.summary_report, {
      worker: true,
      complete: (results: any) => {
        const [headers, ...rest] = results.data;
        setData({ headers, rows: rest });
      },
    });
  }, [readString, reportDaily]);

  const handleSubmitDisalbeColumnSelect = useCallback((values: any) => {
    setDisableColumns(values.disableColumns);
  }, []);

  const handleSubmitRowHeightSetting = useCallback((values: any) => {
    setRowHeight(values.rowHeight);
  }, []);

  const tableRows = useMemo(() => {
    return data.rows.map((row) => {
      return row.map((column) => {
        return {
          type: "TEXT",
          value: column,
        } as ColumnData;
      });
    });
  }, [data]);

  const tableFooterRow = useMemo(() => {
    return [
      { type: "TEXT", value: "合計" },
      {
        type: "TEXT",
        value: reportDaily.aggregated_report.unique_user as string,
      },
      {
        type: "TEXT",
        value: reportDaily.aggregated_report.conversion_user as string,
      },
      {
        type: "TEXT",
        value: reportDaily.aggregated_report.conversion_user_rate as string,
      },
      {
        type: "TEXT",
        value: reportDaily.aggregated_report.lp_drop_rate as string,
      },
      {
        type: "TEXT",
        value: reportDaily.aggregated_report.lp_scroll_rate as string,
      },
      {
        type: "TEXT",
        value: reportDaily.aggregated_report.lp_average_stay_time as string,
      },
      {
        type: "TEXT",
        value: reportDaily.aggregated_report.form_drop_rate as string,
      },
      { type: "TEXT", value: reportDaily.aggregated_report.mobile as string },
      { type: "TEXT", value: reportDaily.aggregated_report.tablet as string },
      { type: "TEXT", value: reportDaily.aggregated_report.pc as string },
    ] as ColumnData[];
  }, [reportDaily]);

  const csvFooterRow = useMemo(() => {
    return [
      "合計",
      reportDaily.aggregated_report.unique_user.replaceAll(",", ""),
      reportDaily.aggregated_report.conversion_user.replaceAll(",", ""),
      reportDaily.aggregated_report.conversion_user_rate,
      reportDaily.aggregated_report.lp_drop_rate,
      reportDaily.aggregated_report.lp_scroll_rate,
      reportDaily.aggregated_report.lp_average_stay_time,
      reportDaily.aggregated_report.form_drop_rate,
      reportDaily.aggregated_report.mobile,
      reportDaily.aggregated_report.tablet,
      reportDaily.aggregated_report.pc,
    ].join(",");
  }, [reportDaily]);

  return (
    <>
      <Flex justifyContent="flex-end" mb="24px" mt={6}>
        <RowHeightSettingButton
          formValues={{ rowHeight }}
          onSubmit={handleSubmitRowHeightSetting}
        />
        <DisableColumnSelectButton
          formValues={{ disableColumns }}
          onSubmit={handleSubmitDisalbeColumnSelect}
          options={disableColumnOptions}
        />
        <CSVDownloadLink
          name={"日別レポート"}
          data={reportDaily.summary_report + csvFooterRow}
          filteredColumns={disableColumns}
        />
      </Flex>
      <Table>
        <ReportTableHeaderRow
          disableColumns={disableColumns}
          onSortClick={onSortClick}
          headerOptions={headerOptionsForDailyReport}
        />
        {tableRows.map((row, i) => (
          <ReportTableDataRow
            key={i}
            row={row}
            disableColumns={disableColumns}
          />
        ))}
        <ReportTableDataRow
          disableColumns={disableColumns}
          row={tableFooterRow}
          rowHeight={`${rowHeight}px`}
        />
      </Table>
    </>
  );
};
